import React from 'react'

/**
 * Reusable editor for defining input variable mappings on chain steps.
 *
 * Edits an inputMap object: { type, mappings: [{ name, to, default }] }
 * Companion to OutputMapEditor — same grid styling for visual consistency.
 *
 * Props:
 *   inputMap           — current inputMap object (or null)
 *   onChange           — callback(newInputMap)
 *   availableVarNames  — variable names accumulated from previous steps' outputMaps
 *   pluginType         — default value for the `type` field
 */

const fieldStyle = {
  padding: '4px 8px',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '13px',
  boxSizing: 'border-box',
}

export const InputMapEditor = ({ inputMap, onChange, availableVarNames, pluginType }) => {
  const currentMap = inputMap || { type: pluginType || '', mappings: [] }
  const mappings = currentMap.mappings || []

  const updateType = (newType) => {
    onChange({ ...currentMap, type: newType })
  }

  const updateMapping = (index, field, value) => {
    const updated = mappings.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    onChange({ ...currentMap, mappings: updated })
  }

  const removeMapping = (index) => {
    const updated = mappings.filter((_, i) => i !== index)
    if (updated.length === 0 && !currentMap.type) {
      onChange(null)
    } else {
      onChange({ ...currentMap, mappings: updated })
    }
  }

  const addMapping = () => {
    const newMapping = { name: '', to: '', default: '' }
    onChange({ ...currentMap, mappings: [...mappings, newMapping] })
  }

  const vars = availableVarNames || []

  return (
    <div>
      <label style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px', display: 'block' }}>
        Input Map (from previous steps)
      </label>
      <p style={{ fontSize: '12px', color: '#6c757d', margin: '0 0 8px 0' }}>
        Map named variables from earlier steps into this simulation&apos;s input state.
      </p>

      {/* Type field */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <label style={{ fontSize: '12px', fontWeight: 600, color: '#6c757d', flexShrink: 0 }}>Type:</label>
        <input
          type="text"
          value={currentMap.type || ''}
          onChange={(e) => updateType(e.target.value)}
          style={{ ...fieldStyle, width: '200px' }}
          placeholder={pluginType || 'plugin-type'}
        />
      </div>

      {mappings.length > 0 ? (
        <div>
          {/* Header row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '160px 200px 140px 30px',
            gap: '4px',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 600,
            color: '#6c757d',
          }}>
            <span>Variable Name</span>
            <span>Target Path</span>
            <span>Default</span>
            <span />
          </div>
          {mappings.map((mapping, index) => {
            const nameNotAvailable = mapping.name && vars.length > 0 && !vars.includes(mapping.name)
            return (
              <div key={index} style={{
                display: 'grid',
                gridTemplateColumns: '160px 200px 140px 30px',
                gap: '4px',
                marginBottom: '4px',
                alignItems: 'center',
              }}>
                <div style={{ position: 'relative' }}>
                  <select
                    value={vars.includes(mapping.name) ? mapping.name : '__other__'}
                    onChange={(e) => {
                      if (e.target.value === '__other__') return
                      updateMapping(index, 'name', e.target.value)
                    }}
                    style={{
                      ...fieldStyle,
                      width: '100%',
                      display: vars.length > 0 && !nameNotAvailable ? 'block' : 'none',
                    }}
                  >
                    <option value="">-- select --</option>
                    {vars.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                    <option value="__other__">Other...</option>
                  </select>
                  <input
                    type="text"
                    value={mapping.name || ''}
                    onChange={(e) => updateMapping(index, 'name', e.target.value)}
                    style={{
                      ...fieldStyle,
                      width: '100%',
                      display: vars.length === 0 || nameNotAvailable ? 'block' : 'none',
                      borderColor: nameNotAvailable ? '#ffc107' : '#ced4da',
                    }}
                    placeholder="variable_name"
                  />
                  {nameNotAvailable && (
                    <span title="This variable is not defined in any previous step's output map" style={{
                      position: 'absolute',
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#ffc107',
                      fontSize: '14px',
                      cursor: 'help',
                    }}>&#9888;</span>
                  )}
                </div>
                <input
                  type="text"
                  value={mapping.to || ''}
                  onChange={(e) => updateMapping(index, 'to', e.target.value)}
                  style={{ ...fieldStyle, fontFamily: 'monospace', fontSize: '12px' }}
                  placeholder="data.path"
                />
                <input
                  type="text"
                  value={mapping.default || ''}
                  onChange={(e) => updateMapping(index, 'default', e.target.value)}
                  style={fieldStyle}
                  placeholder="fallback"
                />
                <button
                  type="button"
                  onClick={() => removeMapping(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc3545',
                    cursor: 'pointer',
                    fontSize: '14px',
                    padding: '0',
                    textAlign: 'center',
                  }}
                >
                  &times;
                </button>
              </div>
            )
          })}
        </div>
      ) : (
        <div style={{ fontSize: '12px', color: '#adb5bd', marginBottom: '4px' }}>
          No input mappings defined. Add entries to inject variables from previous steps.
        </div>
      )}

      <button
        type="button"
        onClick={addMapping}
        className="btn btn-myed btn-default btn-default-myed btn-sm"
        style={{ marginTop: '4px' }}
      >
        + Add Mapping
      </button>
    </div>
  )
}
